import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowUp,
  Megaphone,
  MessageCircle,
  Search,
  Smile,
  SquarePen,
  Users,
  X,
} from 'lucide-react';
import {
  CLUB_SENDER_ID,
  broadcastsOf,
  clockLabel,
  conversationById,
  conversationTitle,
  conversationsFor,
  groupByDay,
  messagesOf,
  previewOf,
  timeLabel,
  unreadCount,
  type Conversation,
  type MessagingState,
} from '../../engine/messaging';
import { bubbleStyle, nameSourceFor } from '../mobile/messagingUi';
import { initials } from '../mobile/mobileUi';
import { memberClubsOf } from '../mobile/memberData';
import {
  clubGroupsFor,
  conversationBlockedBy,
  isMessagingDisabled,
  messagingPlayers,
} from '../../engine/messagingGroups';
import { MessagingOffCard, MessagingOffLink } from './MessagingSwitch';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import { ConversationAvatar } from '../mobile/screens/MobileMessages';
import { EmojiPicker, insertEmoji } from '../mobile/EmojiPicker';
import { autoEmoji, autoEmojiAt } from '../../lib/emoticons';
import type { MessagingApi } from '../../state/useMessaging';

export interface MessagesViewProps {
  messaging: MessagingApi;
  effectivePlayerDB: any[];
  clubInfo: any;
  onBack: () => void;
  /** Injectable clock for deterministic renders. */
  now?: number;
}

type Pane = 'inbox' | 'groups' | 'broadcasts';

// «Meldinger» for the club — Messenger-style two-pane view where the CLUB is
// the sender: every member conversation on the left (unread first), the thread
// on the right, «Ny melding» to write to one member, and «Send til alle» to
// broadcast one text into every member's club conversation.
export function MessagesView({
  messaging,
  effectivePlayerDB,
  clubInfo,
  onBack,
  now,
}: MessagesViewProps) {
  const nowMs = now ?? Date.now();
  const { state } = messaging;
  const names = useMemo(
    () => nameSourceFor(effectivePlayerDB, clubInfo),
    [effectivePlayerDB, clubInfo],
  );
  const me = CLUB_SENDER_ID;
  const [pane, setPane] = useState<Pane>('inbox');
  const [openId, setOpenId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [picking, setPicking] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const clubOff = isMessagingDisabled(state, me);

  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return conversationsFor(state, me)
      .map((c) => ({
        c,
        title: conversationTitle(c, me, names),
        preview: previewOf(state, c, me, names),
        unread: unreadCount(state, c, me),
      }))
      .filter(
        (r) => !n || r.title.toLowerCase().includes(n) || r.preview.toLowerCase().includes(n),
      );
  }, [state, me, names, q]);
  // The automatic club groups («Alle», «Nivå N»), as the club sees them.
  const clubs = useMemo(() => memberClubsOf(clubInfo), [clubInfo]);
  const groupRows = useMemo(
    () =>
      clubGroupsFor(state, clubs, effectivePlayerDB || []).map((c) => ({
        c,
        title: conversationTitle(c, me, names),
        preview: previewOf(state, c, me, names),
        unread: unreadCount(state, c, me),
      })),
    [state, clubs, effectivePlayerDB, me, names],
  );
  const groupsUnread = groupRows.reduce((n, r) => n + r.unread, 0);
  // «Samtaler» shows the automatic groups only once they have messages;
  // «Grupper» always lists all of them.
  const activeGroups = groupRows.filter((r) => r.c.lastMessageAt > 0);
  const inboxRows = [...rows, ...activeGroups].sort(
    (a, b) => b.c.lastMessageAt - a.c.lastMessageAt || a.c.id.localeCompare(b.c.id),
  );
  const listRows = onlyUnread ? inboxRows.filter((r) => r.unread > 0) : inboxRows;
  const openGroup = async (c: Conversation) => {
    // A virtual group gets its stored record on first open, so posting works.
    if (c.rule && !conversationById(state, c.id)) await messaging.openDynamic(c.rule);
    setOpenId(c.id);
  };
  // THE TAB COUNTS WHAT THE TAB LISTS.
  //
  // This summed `rows` alone, and the comment said groups were "counted on
  // their own tab". That was true while «Samtaler» did not show them. It now
  // does — active groups are merged into `inboxRows` above, each rendering its
  // own unread badge — so counting only the DMs left the member with an
  // unbadged tab containing unread groups.
  //
  // The two tabs are not a partition and never were: «Grupper» lists every
  // group, «Samtaler» lists the DMs plus the groups that have messages. Each
  // counting its own contents is what makes either badge mean anything.
  const totalUnread = inboxRows.reduce((n, r) => n + r.unread, 0);
  // Derived groups win over their bare stored record (which has no members/title).
  const open = openId
    ? groupRows.find((r) => r.c.id === openId)?.c || conversationById(state, openId) || null
    : null;
  const broadcasts = useMemo(() => broadcastsOf(state), [state]);
  const activePlayers = useMemo(
    () => messagingPlayers(state, effectivePlayerDB || []),
    [state, effectivePlayerDB],
  );
  const memberCount = activePlayers.length;

  // Opening a thread marks it read for the club.
  useEffect(() => {
    if (open && unreadCount(state, open, me) > 0) void messaging.read(open.id, me);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.id, state]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 slide-up">
      <button onClick={onBack} className="btn-ghost mb-4 -ml-2">
        <ArrowLeft size={14} /> Hjem
      </button>

      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
            {names.clubName} · til medlemmene
          </div>
          <h1 className="font-display text-3xl font-semibold mb-1">Meldinger</h1>
          <p className="text-ink-2 text-sm max-w-xl">
            Skriv til ett medlem, eller send én melding til alle. Medlemmene svarer fra appen, og
            svarene havner her.
          </p>
        </div>
        <div className={`flex items-center gap-2 ${clubOff ? 'hidden' : ''}`}>
          <button onClick={() => setPicking(true)} className="btn-outline text-sm">
            <SquarePen size={14} /> Ny melding
          </button>
          <button onClick={() => setBroadcasting(true)} className="btn-primary text-sm">
            <Megaphone size={14} /> Send til alle
          </button>
        </div>
      </div>

      {clubOff && (
        <MessagingOffCard subject="klubben" onSet={(d) => messaging.setDisabled(me, d)} />
      )}

      <div className={`grid lg:grid-cols-[22rem_1fr] gap-4 items-start ${clubOff ? 'hidden' : ''}`}>
        {/* Left: inbox / broadcasts */}
        <aside className="card overflow-hidden">
          <div className="flex p-1.5 gap-1" style={{ background: 'var(--bg-soft)' }}>
            {(
              [
                { id: 'inbox', label: 'Samtaler', count: totalUnread },
                { id: 'groups', label: 'Grupper', count: groupsUnread },
                { id: 'broadcasts', label: 'Sendt til alle', count: 0 },
              ] as { id: Pane; label: string; count: number }[]
            ).map((p) => (
              <button
                key={p.id}
                onClick={() => setPane(p.id)}
                className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-colors ${
                  pane === p.id ? 'bg-paper text-ink shadow-sm' : 'text-ink-2'
                }`}
                aria-pressed={pane === p.id}
              >
                {p.label}
                {p.count > 0 && (
                  <span
                    className="ml-1 font-mono text-[10px] px-1.5 rounded-full"
                    style={{ background: 'var(--lime)', color: 'var(--ink)' }}
                  >
                    {p.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {pane === 'inbox' && (
            <>
              <label className="relative block px-3 py-2 border-b border-line">
                <Search size={13} className="absolute left-5 top-1/2 -translate-y-1/2 text-ink-3" />
                <input
                  className="input text-sm py-1.5 w-full"
                  style={{ paddingLeft: '2rem' }}
                  placeholder="Søk medlem …"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label="Søk i samtaler"
                />
              </label>
              <div className="flex gap-1 px-3 py-1.5 border-b border-line">
                {[
                  { id: false, label: 'Alle' },
                  { id: true, label: 'Uleste' },
                ].map((f) => (
                  <button
                    key={String(f.id)}
                    onClick={() => setOnlyUnread(f.id)}
                    aria-pressed={onlyUnread === f.id}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      onlyUnread === f.id
                        ? 'bg-ink text-white border-ink'
                        : 'bg-paper text-ink-2 border-line hover:bg-bg-soft'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="max-h-[60vh] overflow-y-auto divide-y divide-line">
                {listRows.length === 0 && (
                  <div className="p-6 text-center text-sm text-ink-2">
                    <MessageCircle size={22} className="mx-auto mb-2 text-ink-4" />
                    {q
                      ? 'Ingen samtaler matcher.'
                      : onlyUnread
                        ? 'Ingenting ulest.'
                        : 'Ingen samtaler ennå. Start med «Ny melding» eller «Send til alle».'}
                  </div>
                )}
                {listRows.map(({ c, title, preview, unread }) => (
                  <button
                    key={c.id}
                    onClick={() => (c.kind === 'dynamic' ? void openGroup(c) : setOpenId(c.id))}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-bg-soft/60 transition-colors ${
                      openId === c.id ? 'bg-bg-soft' : ''
                    }`}
                    aria-current={openId === c.id ? 'true' : undefined}
                  >
                    {c.kind === 'dynamic' ? (
                      <ConversationAvatar conversation={c} title={title} size={36} />
                    ) : (
                      <span
                        className="w-9 h-9 rounded-full flex items-center justify-center font-display font-semibold text-xs flex-shrink-0"
                        style={{ background: 'var(--line)', color: 'var(--ink)' }}
                        aria-hidden
                      >
                        {initials(title)}
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="flex items-baseline justify-between gap-2">
                        <span
                          className={`text-sm truncate ${unread ? 'font-bold' : 'font-medium'}`}
                        >
                          {title}
                        </span>
                        <span className="font-mono text-[10px] text-ink-3 flex-shrink-0">
                          {timeLabel(c.lastMessageAt, nowMs)}
                        </span>
                      </span>
                      <span className="flex items-center justify-between gap-2">
                        <span className={`text-xs truncate ${unread ? 'text-ink' : 'text-ink-3'}`}>
                          {preview}
                        </span>
                        {unread > 0 && (
                          <span
                            className="font-mono text-[10px] font-bold px-1.5 rounded-full flex-shrink-0"
                            style={{ background: 'var(--lime)', color: 'var(--ink)' }}
                          >
                            {unread}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {pane === 'groups' && (
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-line">
              <div className="px-3 py-2 text-[11px] text-ink-3 border-b border-line">
                Automatiske grupper — medlemmene følger sitt faktiske nivå. Klubben kan lese og
                skrive i alle.
              </div>
              {groupRows.map(({ c, title, preview, unread }) => (
                <button
                  key={c.id}
                  onClick={() => void openGroup(c)}
                  className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-bg-soft/60 transition-colors ${
                    openId === c.id ? 'bg-bg-soft' : ''
                  }`}
                  aria-current={openId === c.id ? 'true' : undefined}
                  data-testid="club-group-row"
                >
                  <ConversationAvatar conversation={c} title={title} size={36} />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className={`text-sm truncate ${unread ? 'font-bold' : 'font-medium'}`}>
                        {title}
                      </span>
                      <span className="font-mono text-[10px] text-ink-3 flex-shrink-0">
                        {c.lastMessageAt ? timeLabel(c.lastMessageAt, nowMs) : ''}
                      </span>
                    </span>
                    <span className="flex items-center justify-between gap-2">
                      <span className={`text-xs truncate ${unread ? 'text-ink' : 'text-ink-3'}`}>
                        {c.lastMessageAt
                          ? preview
                          : `${c.participantIds.length} medlem${c.participantIds.length === 1 ? '' : 'mer'}`}
                      </span>
                      {unread > 0 && (
                        <span
                          className="font-mono text-[10px] font-bold px-1.5 rounded-full flex-shrink-0"
                          style={{ background: 'var(--lime)', color: 'var(--ink)' }}
                        >
                          {unread}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {pane === 'broadcasts' && (
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-line">
              {broadcasts.length === 0 && (
                <div className="p-6 text-center text-sm text-ink-2">
                  <Megaphone size={22} className="mx-auto mb-2 text-ink-4" />
                  Ingenting sendt til alle ennå.
                </div>
              )}
              {broadcasts.map((b) => (
                <div key={b.broadcastId} className="px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-2 mb-0.5">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                      Til {b.count} medlem{b.count === 1 ? '' : 'mer'}
                    </span>
                    <span className="font-mono text-[10px] text-ink-3">
                      {timeLabel(b.sentAt, nowMs)}
                    </span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{b.text}</div>
                </div>
              ))}
            </div>
          )}
          <div className="px-3 py-2.5 border-t border-line text-center">
            <MessagingOffLink subject="klubben" onSet={(d) => messaging.setDisabled(me, d)} />
          </div>
        </aside>

        {/* Right: thread */}
        <section className="card min-h-[60vh] flex flex-col">
          {!open && (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-ink-2">
              <MessageCircle size={28} className="mb-2 text-ink-4" />
              <div className="font-display text-lg font-semibold text-ink mb-1">
                Velg en samtale
              </div>
              <p className="text-sm max-w-xs">
                Samtalene med medlemmene ligger til venstre. Uleste er uthevet.
              </p>
            </div>
          )}
          {open && (
            <ClubThread
              key={open.id}
              state={state}
              conversation={open}
              names={names}
              nowMs={nowMs}
              onSend={(text) => messaging.send(open.id, me, text)}
            />
          )}
        </section>
      </div>

      {picking && (
        <MemberPickModal
          players={activePlayers}
          onClose={() => setPicking(false)}
          onPick={async (id) => {
            const conv = await messaging.openClubConversation(id);
            setPicking(false);
            setPane('inbox');
            setOpenId(conv.id);
          }}
        />
      )}

      {broadcasting && (
        <BroadcastModal
          memberCount={memberCount}
          clubName={names.clubName}
          onClose={() => setBroadcasting(false)}
          onSend={async (text) => {
            await messaging.broadcast(
              activePlayers.map((p: any) => p.id),
              text,
            );
            setBroadcasting(false);
            setPane('broadcasts');
          }}
        />
      )}
    </div>
  );
}

export function ClubThread({
  state,
  conversation,
  names,
  nowMs,
  onSend,
  compact = false,
}: {
  state: MessagingState;
  conversation: Conversation;
  names: ReturnType<typeof nameSourceFor>;
  nowMs: number;
  onSend: (text: string) => Promise<unknown>;
  /** Dock mode: fill the available height instead of the page layout's cap. */
  compact?: boolean;
}) {
  const me = CLUB_SENDER_ID;
  const title = conversationTitle(conversation, me, names);
  const messages = useMemo(() => messagesOf(state, conversation.id), [state, conversation.id]);
  const days = useMemo(() => groupByDay(messages, nowMs), [messages, nowMs]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);
  const pickEmoji = (e: string) => {
    const { text, caret } = insertEmoji(draft, e, inputRef.current);
    setDraft(text);
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const send = async () => {
    const text = autoEmoji(draft.trim());
    if (!text || busy) return;
    setBusy(true);
    try {
      await onSend(text);
      setDraft('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!compact && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
          <span
            className="w-9 h-9 rounded-full flex items-center justify-center font-display font-semibold text-xs"
            style={{ background: 'var(--line)', color: 'var(--ink)' }}
            aria-hidden
          >
            {initials(title)}
          </span>
          <div>
            <div className="font-display font-semibold leading-tight">{title}</div>
            <div className="text-[11px] text-ink-3">
              {conversation.kind === 'dynamic'
                ? `Automatisk gruppe · ${conversation.participantIds.length} medlem${conversation.participantIds.length === 1 ? '' : 'mer'}`
                : 'Samtale mellom klubben og medlemmet'}
            </div>
          </div>
        </div>
      )}
      <div
        className={`flex-1 overflow-y-auto px-4 py-3 space-y-3 ${compact ? '' : 'max-h-[52vh]'}`}
      >
        {days.length === 0 && (
          <div className="text-center text-xs text-ink-3 py-8">Ingen meldinger ennå.</div>
        )}
        {days.map((d) => (
          <div key={d.dayKey} className="space-y-1.5">
            <div className="text-center font-mono text-[10px] uppercase tracking-wider text-ink-3 py-1">
              {d.label}
            </div>
            {d.messages.map((m, i) => {
              const mine = m.senderId === me;
              const prev = d.messages[i - 1];
              const showName =
                conversation.kind === 'dynamic' && !mine && (!prev || prev.senderId !== m.senderId);
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[70%]">
                    {showName && (
                      <div className="text-[10px] text-ink-3 px-2 mb-0.5">
                        {names.nameOf(m.senderId) || 'Ukjent'}
                      </div>
                    )}
                    <div
                      className="rounded-2xl px-3 py-2 text-sm leading-snug whitespace-pre-wrap break-words"
                      style={{
                        ...bubbleStyle(mine ? 'me' : m.senderId, 'me'),
                        ...(mine ? { background: 'var(--ink)', color: '#fff' } : {}),
                        borderBottomRightRadius: mine ? 6 : undefined,
                        borderBottomLeftRadius: mine ? undefined : 6,
                      }}
                    >
                      {m.text}
                      <span
                        className="block text-right font-mono text-[9px] mt-0.5"
                        style={{ opacity: 0.6 }}
                      >
                        {clockLabel(m.sentAt)}
                        {m.broadcastId ? ' · til alle' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      {conversationBlockedBy(state, conversation, me) ? (
        <div
          className="px-4 py-3 text-center text-xs text-ink-3 border-t border-line"
          data-testid="chat-blocked"
        >
          {title} har slått av meldinger. Samtalen er lukket til det slås på igjen.
        </div>
      ) : (
        <div className="flex items-end gap-2 px-3 py-2 border-t border-line">
          <div className="relative flex-shrink-0">
            {emojiOpen && <EmojiPicker onPick={pickEmoji} onClose={() => setEmojiOpen(false)} />}
            <button
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              className="btn-ghost p-2 rounded-full"
              aria-label="Emoji"
              aria-expanded={emojiOpen}
            >
              <Smile size={18} />
            </button>
          </div>
          <textarea
            ref={inputRef}
            className="input flex-1 text-sm resize-none"
            rows={2}
            placeholder={`Skriv til ${title} …`}
            value={draft}
            onChange={(e) => {
              // «:)» → 😊 while typing (whole tokens only — see lib/emoticons).
              const el = e.target;
              const { text, caret } = autoEmojiAt(el.value, el.selectionStart ?? el.value.length);
              setDraft(text);
              if (text !== el.value)
                requestAnimationFrame(() => inputRef.current?.setSelectionRange(caret, caret));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            aria-label="Skriv en melding"
          />
          <button
            onClick={() => void send()}
            disabled={!draft.trim() || busy}
            className="btn-primary p-2.5 rounded-full"
            aria-label="Send"
          >
            <ArrowUp size={16} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </>
  );
}

export function MemberPickModal({
  players,
  onClose,
  onPick,
}: {
  players: any[];
  onClose: () => void;
  onPick: (id: any) => Promise<void>;
}) {
  const [q, setQ] = useState('');
  const uid = safeId('member-pick', useId());
  const ids = { title: `${uid}-title`, lead: `${uid}-lead` };
  // Focus starts in the search field, where the old `autoFocus` put it.
  const searchRef = useRef<HTMLInputElement | null>(null);
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (players || [])
      .filter(
        (p: any) =>
          p?.id != null &&
          (!n ||
            String(p.name || '')
              .toLowerCase()
              .includes(n)),
      )
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''), 'nb'))
      .slice(0, 60);
  }, [players, q]);
  return (
    // A `Dialog` (#900). The role used to sit on the BACKDROP — the surface
    // that closes it — and nothing kept focus inside or gave it back. Named by
    // its title, described by the line under it; Escape closes it the way the
    // backdrop does.
    <Dialog
      labelledBy={ids.title}
      describedBy={ids.lead}
      onClose={onClose}
      showClose={false}
      initialFocus={searchRef}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4"
      backdropStyle={{ background: 'rgba(15,22,17,0.45)' }}
      className="card-elev w-full max-w-md p-4 max-h-[80vh] flex flex-col"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 id={ids.title} className="font-display text-xl font-semibold">
            Ny melding
          </h2>
          <p id={ids.lead} className="text-xs text-ink-2">
            Velg medlemmet du vil skrive til.
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Lukk" className="btn-ghost p-1">
          <X size={16} />
        </button>
      </div>
      <input
        ref={searchRef}
        className="input w-full text-sm mb-2"
        placeholder="Søk medlem …"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Søk medlem"
      />
      <div className="overflow-y-auto divide-y divide-line">
        {list.length === 0 && (
          <div className="p-4 text-center text-sm text-ink-2">Ingen medlemmer matcher.</div>
        )}
        {list.map((p: any) => (
          <button
            key={p.id}
            type="button"
            onClick={() => void onPick(p.id)}
            className="w-full text-left px-2 py-2 flex items-center gap-3 hover:bg-bg-soft/60"
          >
            <span
              className="w-8 h-8 rounded-full flex items-center justify-center font-display font-semibold text-xs"
              style={{ background: 'var(--line)', color: 'var(--ink)' }}
              aria-hidden
            >
              {initials(p.name)}
            </span>
            <span className="text-sm">{p.name}</span>
          </button>
        ))}
      </div>
    </Dialog>
  );
}

function BroadcastModal({
  memberCount,
  clubName,
  onClose,
  onSend,
}: {
  memberCount: number;
  clubName: string;
  onClose: () => void;
  onSend: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const uid = safeId('broadcast', useId());
  const ids = { title: `${uid}-title`, lead: `${uid}-lead` };
  // Focus starts in the message field, where the old `autoFocus` put it.
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await onSend(text);
    } catch {
      setError('Kunne ikke sende meldingen.');
    } finally {
      setBusy(false);
    }
  };
  return (
    // A `Dialog` (#900) — see `MemberPickModal`. Escape is «Avbryt».
    <Dialog
      labelledBy={ids.title}
      describedBy={ids.lead}
      onClose={onClose}
      showClose={false}
      initialFocus={textRef}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center p-4"
      backdropStyle={{ background: 'rgba(15,22,17,0.45)' }}
      className="card-elev w-full max-w-lg p-5 overflow-y-auto max-h-[92vh]"
      style={{ maxHeight: 'calc(100dvh - 2rem)' }}
    >
      <div className="flex items-start justify-between mb-1">
        <h2 id={ids.title} className="font-display text-xl font-semibold flex items-center gap-2">
          <Megaphone size={18} /> Send til alle
        </h2>
        <button type="button" onClick={onClose} aria-label="Lukk" className="btn-ghost p-1">
          <X size={16} />
        </button>
      </div>
      <p id={ids.lead} className="text-sm text-ink-2 mb-3 flex items-center gap-1.5">
        <Users size={13} /> Meldingen går som «{clubName}» til {memberCount} medlem
        {memberCount === 1 ? '' : 'mer'} — hver får den i sin egen samtale og kan svare.
      </p>
      <textarea
        ref={textRef}
        className="input w-full text-sm"
        rows={5}
        placeholder="F.eks. «Banene stenger kl. 22 i kveld pga. vedlikehold.»"
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label="Melding til alle"
      />
      {error && (
        <div className="text-xs mt-2" style={{ color: 'var(--danger)' }}>
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" onClick={onClose} className="btn-outline text-sm">
          Avbryt
        </button>
        <button
          type="button"
          onClick={() => void send()}
          disabled={!text.trim() || busy || memberCount === 0}
          className="btn-primary text-sm"
        >
          {busy ? 'Sender …' : `Send til ${memberCount} medlem${memberCount === 1 ? '' : 'mer'}`}
        </button>
      </div>
    </Dialog>
  );
}
